import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Slider from "components/DesignSystem/Slider/Slider";
import {
  Accordion,
  ItemGrid,
} from "components/DesignSystem/Accordion/Accordion";
import { SliderNavigation } from "components/DesignSystem/SliderNavigation/SliderNavigation";
import { Label } from "components/DesignSystem/TextInput/TextInput";
import { Cross } from "components/icons/Cross";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { ReactNode, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useGetRuleByIdQuery } from "store/apis/ruleEngine";
import { closeSlider, openSlider } from "store/slices/ruleEngine";
import { RootState } from "store/store";
import { Actions, ValueType } from "./RuleEngine";
import { RULE_PRIORITY_OPTIONS, RULE_SOURCE } from "constants/ruleEngine";

const InfoItem = ({
  label,
  children,
}: {
  label: string | ReactNode;
  children: ReactNode;
}) => {
  return (
    <div className="t-flex t-flex-col t-gap-1">
      <Label className="!t-pb-0">{label}</Label>
      <span className="t-text-subtext">{children}</span>
    </div>
  );
};

const useSilderRule = () => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const { previewRuleId } = useSelector((store: RootState) => store.ruleEngine);

  return useGetRuleByIdQuery(
    { entityId, groupId, ruleId: previewRuleId },
    { skip: !groupId || !entityId || !previewRuleId }
  );
};

const Header = () => {
  const dispatch = useDispatch();
  const { data } = useSilderRule();
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.TRANSACTIONS
  );
  const { name } = data || {};

  const { previewRuleId, allRuleIds } = useSelector(
    (store: RootState) => store.ruleEngine
  );

  const close = () => {
    dispatch(closeSlider());
  };

  const currentRuleIndex = allRuleIds.findIndex(
    (ruleId) => ruleId === previewRuleId
  );

  const dataLength = allRuleIds.length;

  const goToNext = () => {
    if (currentRuleIndex + 1 < allRuleIds.length) {
      dispatch(openSlider(allRuleIds[currentRuleIndex + 1]));
    }
  };

  const goToPrev = () => {
    if (currentRuleIndex > 0) {
      dispatch(openSlider(allRuleIds[currentRuleIndex - 1]));
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case "ArrowLeft":
          goToPrev();
          return;

        case "ArrowRight":
          goToNext();
          return;

        default:
          return;
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentRuleIndex]);

  return (
    <Slider.Header>
      <Slider.Title>{name}</Slider.Title>
      <div className="t-flex t-gap-2 t-items-center">
        <SliderNavigation
          dataLength={dataLength}
          currentIndex={currentRuleIndex}
          onPrev={goToPrev}
          onNext={goToNext}
        />
        <Actions rule={data!} hasWritePermission={hasWritePermission} />
        <Button customType="ghost_icon" size="small" onClick={close}>
          <Cross color="currentColor" />
        </Button>
      </div>
    </Slider.Header>
  );
};

const SliderBody = () => {
  const { data, isLoading, isSuccess } = useSilderRule();
  const {
    name,
    end_date,
    start_date,
    conditions,
    category,
    merchant_data,
    classification_data,
    source,
    priority,
    created_by,
    apply_to_future_txns,
    apply_on_all_past_txns,
  } = data || {};
  const priorityLabel =
    RULE_PRIORITY_OPTIONS.find((opt) => opt.value === priority)?.label ?? "-";

  return (
    <Async.Root {...{ isEmpty: false, isLoading, isSuccess }}>
      <Async.Empty>
        <></>
      </Async.Empty>
      <Async.Success>
        <div className="t-flex t-flex-col">
          <Header />

          <div className="t-p-5 t-flex t-gap-4 t-flex-col t-pb-24">
            <Accordion.Root
              type="multiple"
              defaultValue={["DETAILS", "CONDITIONS", "ASSIGNMENT"]}
              className="t-flex t-gap-4 t-flex-col"
            >
              <Accordion.Item value="DETAILS">
                <Accordion.Trigger>Details</Accordion.Trigger>
                <Accordion.Content>
                  <ItemGrid>
                    <InfoItem label="Rule name">{name}</InfoItem>
                    <InfoItem label="Duration">
                      {apply_to_future_txns && apply_on_all_past_txns
                        ? "All time"
                        : `Start from ${
                            start_date
                              ? dayjs(start_date).format(DISPLAY_DATE)
                              : ""
                          }`}
                    </InfoItem>
                    <InfoItem label="Rule type">
                      {RULE_SOURCE.MANUAL === source
                        ? "User generated"
                        : "System generated"}
                    </InfoItem>
                    <InfoItem label="Rule Priority">{priorityLabel}</InfoItem>
                    <InfoItem label="Created by">
                      {created_by?.name ?? "-"}
                    </InfoItem>
                  </ItemGrid>
                </Accordion.Content>
              </Accordion.Item>

              <Accordion.Item value="CONDITIONS">
                <Accordion.Trigger>Conditions</Accordion.Trigger>
                <Accordion.Content>
                  <span className="t-flex t-gap-4 t-flex-col">
                    {conditions?.map(
                      (
                        {
                          condition_operand,
                          condition_operator,
                          value,
                          value_type,
                          uuid,
                        },
                        i
                      ) => (
                        <InfoItem label={`Condition ${i + 1}`} key={uuid}>
                          {condition_operand.name}
                          <span className="t-lowercase">
                            {" "}
                            {condition_operator.name}
                          </span>{" "}
                          <ValueType
                            tag
                            value={value}
                            value_type={value_type}
                          />
                        </InfoItem>
                      )
                    )}
                  </span>
                </Accordion.Content>
              </Accordion.Item>

              <Accordion.Item value="ASSIGNMENT">
                <Accordion.Trigger>Assignment</Accordion.Trigger>
                <Accordion.Content>
                  <span className="t-flex t-gap-6 t-flex-col">
                    <ItemGrid>
                      <InfoItem label="Category">
                        {category?.name ?? "-"}
                      </InfoItem>
                      <InfoItem label="Vendor">
                        {merchant_data?.name ?? "-"}
                      </InfoItem>
                      <InfoItem label="Classification">
                        {classification_data && classification_data.length > 0
                          ? classification_data.map((classItem) => (
                              <div key={classItem.uuid}>
                                <span className="t-text-text-30 t-text-body-sm">
                                  {classItem.group_name} :{" "}
                                </span>
                                {classItem.name}
                              </div>
                            ))
                          : "-"}
                      </InfoItem>
                    </ItemGrid>

                    {apply_to_future_txns && (
                      <Checkbox
                        label="Apply to future transactions."
                        checked={apply_to_future_txns}
                        disabled
                      />
                    )}
                  </span>
                </Accordion.Content>
              </Accordion.Item>
            </Accordion.Root>
          </div>
        </div>
      </Async.Success>
    </Async.Root>
  );
};

export const RuleSlider = () => {
  const dispatch = useDispatch();

  const { isSliderOpen, previewRuleId } = useSelector(
    (store: RootState) => store.ruleEngine
  );

  const close = () => {
    dispatch(closeSlider());
  };

  return (
    <Slider.Root open={isSliderOpen} onOpenChange={close}>
      <Slider.Content open={isSliderOpen}>
        <SliderBody key={previewRuleId} />
      </Slider.Content>
    </Slider.Root>
  );
};
